import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Rocket, CheckCircle2, Circle, ArrowRight, X, Clock, Sparkles, ChevronRight, ChevronDown,
} from "lucide-react";

/**
 * First-run onboarding wizard.
 * - Renders as an inline card at the top of Dashboard (compact when all done).
 * - Auto-refreshes state on mount + when the user returns to the page.
 * - Admins can dismiss permanently; non-admins see the checklist until steps
 *   are actually complete (state is derived server-side from real data).
 *
 * Props:
 *   - variant: "card" (default, full inline block) | "pill" (compact header pill).
 *   - onDismiss / onOpen: optional callbacks.
 */
export default function OnboardingWizard({ variant = "card", initialOpen = false, onCloseCollapse }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [collapsed, setCollapsed] = useState(!initialOpen);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "Admin";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setStatus(data);
    } catch (e) {
      // Silently ignore — wizard is progressive-enhancement.
      setStatus({ hidden: true });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Refresh on window focus so the checklist updates immediately when the
  // user returns from another tab where they completed a step.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (!status || status.hidden) return null;
  // Admin dismiss hides the card in both complete and incomplete states — one
  // check covers both.
  if (status.dismissed) return null;

  const { steps = [], done = 0, total = 0, pct = 0, complete } = status;
  const remainingSteps = steps.filter((s) => !s.done);
  const nextStep = remainingSteps[0] || null;
  const totalEta = remainingSteps.reduce((acc, s) => acc + (s.eta_min || 0), 0);

  const goStep = (s) => {
    if (!s?.cta_href) return;
    nav(s.cta_href);
  };

  const dismiss = async () => {
    if (!window.confirm("Hide the getting-started wizard? An admin can bring it back later from Tenant Settings.")) return;
    setBusy(true);
    try {
      await api.post("/onboarding/dismiss");
      toast.success("Onboarding wizard hidden");
      await load();
      onCloseCollapse && onCloseCollapse();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  // ------- PILL VARIANT (header) -------
  if (variant === "pill") {
    if (complete) return null;
    return (
      <button
        type="button"
        onClick={() => nav("/dashboard#getting-started")}
        data-testid="onboarding-pill"
        className="hidden md:inline-flex items-center gap-2 text-[11.5px] font-medium rounded-full border border-cyan-200 bg-cyan-50 hover:bg-cyan-100 text-cyan-900 px-2.5 py-1 transition-colors"
        title="Getting started checklist"
      >
        <Sparkles className="h-3.5 w-3.5" />
        <span className="tabular-nums">{done}/{total}</span>
        <span className="hidden lg:inline">Getting started</span>
      </button>
    );
  }

  // ------- CARD VARIANT (Dashboard) -------
  const compact = complete;

  return (
    <div
      id="getting-started"
      data-testid="onboarding-wizard"
      className={`relative mb-6 rounded-2xl border overflow-hidden transition-all ${
        complete
          ? "border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-white"
          : "border-cyan-300 bg-gradient-to-br from-cyan-50 via-white to-white shadow-sm"
      }`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
            complete ? "bg-emerald-600" : "bg-gradient-to-br from-cyan-500 to-blue-600"
          }`}>
            {complete ? <CheckCircle2 className="h-5 w-5 text-white" /> : <Rocket className="h-5 w-5 text-white" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-800">
                {complete ? "All set" : "Getting started"}
              </div>
              {!complete && totalEta > 0 && (
                <span className="inline-flex items-center gap-1 text-[10.5px] font-mono text-slate-600 bg-white/70 border border-slate-200 rounded-full px-1.5 py-0.5">
                  <Clock className="h-3 w-3" /> ~{totalEta} min left
                </span>
              )}
            </div>
            <div className="font-display text-lg font-bold text-slate-900 leading-tight mt-0.5">
              {complete
                ? "Your workspace is live"
                : nextStep ? `Next: ${nextStep.title}` : "Finish setting up your workspace"}
            </div>
            {!complete && nextStep?.description && (
              <div className="text-[13px] text-slate-600 mt-1 leading-snug">{nextStep.description}</div>
            )}
            {complete && (
              <div className="text-[13px] text-slate-600 mt-1">
                Nice work — you've branded the workspace, invited your team, and put your first risk on the record.
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!complete && nextStep && (
            <Button
              size="sm"
              onClick={() => goStep(nextStep)}
              className="bg-[#0A1329] hover:bg-[#162347] text-white h-8"
              data-testid="onboarding-cta-next"
            >
              {nextStep.cta_label || "Start"} <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="h-8 w-8 rounded-md hover:bg-white/70 flex items-center justify-center text-slate-500"
            aria-label={collapsed ? "Expand checklist" : "Collapse checklist"}
            data-testid="onboarding-toggle"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={dismiss}
              disabled={busy}
              className="h-8 w-8 rounded-md hover:bg-white/70 flex items-center justify-center text-slate-400 hover:text-slate-700"
              aria-label="Dismiss wizard"
              data-testid="onboarding-dismiss"
              title="Hide wizard (admins only)"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-5 pb-3">
        <div className="flex items-center justify-between text-[11px] font-mono text-slate-600 mb-1">
          <span className="tabular-nums font-semibold text-slate-800">{done}/{total} steps complete</span>
          <span className="tabular-nums">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-white/70 border border-slate-200 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${complete ? "bg-emerald-500" : "bg-gradient-to-r from-cyan-500 to-blue-600"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Step list */}
      {!collapsed && !compact && (
        <div className="border-t border-slate-200/70 bg-white/40 px-2 py-2">
          <ul className="divide-y divide-slate-100" data-testid="onboarding-steps">
            {steps.map((s, i) => (
              <li key={s.key} data-testid={`onboarding-step-${s.key}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${s.done ? "" : "hover:bg-white cursor-pointer"}`}
                  onClick={() => !s.done && goStep(s)}
                  role={!s.done ? "button" : undefined}
                  tabIndex={!s.done ? 0 : undefined}>
                <div className="shrink-0">
                  {s.done ? (
                    <div className="h-6 w-6 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center">
                      <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                    </div>
                  ) : (
                    <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center text-[10px] font-mono font-bold ${
                      i === done ? "border-cyan-500 text-cyan-700 bg-white" : "border-slate-300 text-slate-500 bg-white"
                    }`}>
                      {i + 1}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`text-[13.5px] font-semibold leading-snug ${s.done ? "text-slate-400 line-through" : "text-slate-900"}`}>
                    {s.title}
                  </div>
                  {!s.done && (
                    <div className="text-[11.5px] text-slate-500 leading-snug mt-0.5 line-clamp-1">{s.description}</div>
                  )}
                </div>
                {!s.done && (
                  <>
                    <span className="hidden sm:inline-flex items-center gap-1 text-[10.5px] font-mono text-slate-500 shrink-0">
                      <Clock className="h-3 w-3" /> ~{s.eta_min}m
                    </span>
                    <Button variant="ghost" size="sm" className="text-cyan-800 hover:text-cyan-900 h-7 px-2 shrink-0"
                      onClick={(e) => { e.stopPropagation(); goStep(s); }}
                      data-testid={`onboarding-cta-${s.key}`}>
                      {s.cta_label} <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
